import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Nav } from "../components/Nav";
import { useAuth } from "../AuthContext";
import { useLang } from "../i18n";
import { AREA_META } from "../constants/areaMeta";
import { api } from "../api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { ArrowLeft, UserRound, RefreshCw, Loader2 } from "lucide-react";

export default function Profile() {
  const { user, setUser } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const areas = Object.keys(AREA_META);

  const [firstName, setFirstName] = useState(user?.first_name || user?.name || "");
  const [lastName, setLastName] = useState(user?.last_name || "");
  const [goal, setGoal] = useState(user?.goal || "");
  const [focus, setFocus] = useState(user?.focus_areas || []);
  const [sports, setSports] = useState(user?.sports || "");
  const [frequency, setFrequency] = useState(user?.frequency || "freq_1_2");
  const [diet, setDiet] = useState(user?.diet_preference || "completo");
  const [age, setAge] = useState(user?.age ? String(user.age) : "");
  const [weight, setWeight] = useState(user?.weight_kg ? String(user.weight_kg) : "");
  const [height, setHeight] = useState(user?.height_cm ? String(user.height_cm) : "");
  const [trainingDays, setTrainingDays] = useState(user?.training_days || []);
  const [conditions, setConditions] = useState(user?.chronic_conditions || "");
  const [pain, setPain] = useState(user?.pain_areas || "");
  const [busy, setBusy] = useState(false);
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenBusy, setRegenBusy] = useState(false);

  const regenerateJourney = async () => {
    setRegenBusy(true);
    try {
      const res = await api.post(`/program/generate?regenerate=true&lang=${lang}`);
      if (res.data.status === "regenerating" || res.data.status === "pending") {
        toast.success(t("regen_started"));
        navigate("/program");
      } else {
        toast.info(t("regen_started"));
        navigate("/program");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || t("regen_no_program"));
    } finally {
      setRegenBusy(false);
      setRegenOpen(false);
    }
  };

  const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
  const toggleDay = (d) => setTrainingDays((v) => (v.includes(d) ? v.filter((x) => x !== d) : [...v, d]));
  const toggle = (a) => setFocus((f) => (f.includes(a) ? f.filter((x) => x !== a) : [...f, a]));

  const save = async () => {
    setBusy(true);
    try {
      const res = await api.patch("/profile", {
        first_name: firstName, last_name: lastName,
        goal, focus_areas: focus, sports, frequency, diet_preference: diet,
        age: age ? parseInt(age, 10) : null,
        weight_kg: weight ? parseFloat(weight) : null,
        height_cm: height ? parseFloat(height) : null,
        training_days: trainingDays,
        chronic_conditions: conditions, pain_areas: pain,
      });
      setUser(res.data);
      toast.success(t("profile_saved"));
      navigate("/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setBusy(false);
    }
  };

  const ageMissing = !user?.age;

  return (
    <div className="App min-h-screen">
      <Nav />
      <div className="max-w-2xl mx-auto px-6 py-10">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-[#6E6B65] mb-4 hover:text-[#3F6E85]" data-testid="profile-back">
          <ArrowLeft className="w-4 h-4" /> {t("back")}
        </button>
        <div className="flex items-center gap-3 mb-2">
          <span className="w-10 h-10 rounded-full bg-[#EAF0EC] flex items-center justify-center"><UserRound className="w-5 h-5 text-[#3F6E85]" /></span>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">{t("profile_title")}</h1>
        </div>
        <p className="text-[#6E6B65] mb-8 max-w-xl">{t("profile_sub")}</p>

        {ageMissing && (
          <div className="rounded-2xl p-4 mb-8 bg-[#FBEEDF] border border-[#f0d3ad] text-sm text-[#7a5a2a]" data-testid="profile-age-alert">
            {t("profile_age_alert")}
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-6 mb-8">
          <div>
            <label htmlFor="profile-first-name" className="block font-semibold mb-2">{t("first_name")}</label>
            <Input id="profile-first-name" autoComplete="given-name" data-testid="profile-first-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} className="rounded-xl" />
          </div>
          <div>
            <label htmlFor="profile-last-name" className="block font-semibold mb-2">{t("last_name")}</label>
            <Input id="profile-last-name" autoComplete="family-name" data-testid="profile-last-name" value={lastName} onChange={(e) => setLastName(e.target.value)} className="rounded-xl" />
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-6 mb-8">
          <div>
            <label className="block font-semibold mb-2">{t("onboard_age")}</label>
            <Input data-testid="profile-age" type="number" min="14" max="110" value={age} onChange={(e) => setAge(e.target.value)} className="rounded-xl" />
          </div>
          <div>
            <label className="block font-semibold mb-2">{t("profile_weight")}</label>
            <Input data-testid="profile-weight" type="number" min="30" max="300" step="0.1" value={weight} onChange={(e) => setWeight(e.target.value)} className="rounded-xl" />
          </div>
          <div>
            <label className="block font-semibold mb-2">{t("profile_height")}</label>
            <Input data-testid="profile-height" type="number" min="100" max="230" value={height} onChange={(e) => setHeight(e.target.value)} className="rounded-xl" />
          </div>
        </div>

        <label className="block font-semibold mb-3">{t("profile_training_days")}</label>
        <div className="flex flex-wrap gap-2 mb-8" data-testid="profile-training-days">
          {WEEKDAYS.map((d) => {
            const active = trainingDays.includes(d);
            return (
              <button key={d} type="button" data-testid={`profile-day-${d}`} onClick={() => toggleDay(d)}
                className={`w-12 h-12 rounded-2xl text-sm font-semibold transition-all ${active ? "bg-[#3F6E85] text-white" : "bg-white border border-stone-200 text-[#6E6B65] hover:border-stone-300"}`}>
                {t(`day_${d}`)}
              </button>
            );
          })}
        </div>

        <div className="grid sm:grid-cols-1 gap-6 mb-8">
          <div>
            <label className="block font-semibold mb-2">{t("onboard_conditions")}</label>
            <Input data-testid="profile-conditions" value={conditions} onChange={(e) => setConditions(e.target.value)} placeholder={t("conditions_placeholder")} className="rounded-xl" />
          </div>
        </div>
        <div className="mb-8">
          <label className="block font-semibold mb-2">{t("onboard_pain")}</label>
          <Input data-testid="profile-pain" value={pain} onChange={(e) => setPain(e.target.value)} placeholder={t("pain_placeholder")} className="rounded-xl" />
        </div>

        <label className="block font-semibold mb-2">{t("goal_label")}</label>
        <Textarea data-testid="profile-goal" value={goal} onChange={(e) => setGoal(e.target.value)} className="rounded-xl mb-8 min-h-[90px]" />

        <label className="block font-semibold mb-3">{t("focus_label")}</label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8">
          {areas.map((a) => {
            const Icon = AREA_META[a].icon;
            const active = focus.includes(a);
            return (
              <button key={a} data-testid={`profile-focus-${a}`} onClick={() => toggle(a)}
                className={`rounded-xl p-4 border text-left transition-all ${active ? "border-[#3F6E85] bg-[#EAF0EC]" : "border-stone-200 bg-white hover:border-stone-300"}`}>
                <Icon className="w-5 h-5 mb-2" style={{ color: AREA_META[a].color }} />
                <span className="text-sm font-medium">{t(a)}</span>
              </button>
            );
          })}
        </div>

        <label className="block font-semibold mb-2">{t("onboard_sports")}</label>
        <Input data-testid="profile-sports" value={sports} onChange={(e) => setSports(e.target.value)} placeholder={t("sports_placeholder")} className="rounded-xl mb-8" />

        <div className="grid sm:grid-cols-2 gap-6 mb-10">
          <div>
            <label className="block font-semibold mb-2">{t("onboard_frequency")}</label>
            <Select value={frequency} onValueChange={setFrequency}>
              <SelectTrigger data-testid="profile-frequency" className="rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="freq_none">{t("freq_none")}</SelectItem>
                <SelectItem value="freq_1_2">{t("freq_1_2")}</SelectItem>
                <SelectItem value="freq_3_4">{t("freq_3_4")}</SelectItem>
                <SelectItem value="freq_5plus">{t("freq_5plus")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block font-semibold mb-2">{t("onboard_diet")}</label>
            <Select value={diet} onValueChange={setDiet}>
              <SelectTrigger data-testid="profile-diet" className="rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="completo">{t("diet_completo")}</SelectItem>
                <SelectItem value="mediterraneo">{t("diet_mediterraneo")}</SelectItem>
                <SelectItem value="vegetariano">{t("diet_vegetariano")}</SelectItem>
                <SelectItem value="vegano">{t("diet_vegano")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <p className="text-xs text-[#6E6B65] mb-8">{t("onboard_health_note")}</p>

        <div className="rounded-2xl p-5 mb-8 bg-[#EEF3F6] border border-[#86A6B8]/30" data-testid="regen-journey-card">
          <div className="flex items-start gap-3">
            <span className="w-10 h-10 rounded-full bg-white flex items-center justify-center shrink-0"><RefreshCw className="w-5 h-5 text-[#3F6E85]" /></span>
            <div className="flex-1">
              <p className="font-semibold text-[#2B4A59]">{t("regen_title")}</p>
              <p className="text-sm text-[#6E6B65] mt-0.5 mb-3">{t("regen_desc")}</p>
              <Button variant="outline" onClick={() => setRegenOpen(true)} className="rounded-full gap-2 border-[#3F6E85] text-[#3F6E85] hover:bg-[#3F6E85] hover:text-white" data-testid="regen-journey-btn">
                <RefreshCw className="w-4 h-4" /> {t("regen_button")}
              </Button>
            </div>
          </div>
        </div>

        <AlertDialog open={regenOpen} onOpenChange={setRegenOpen}>
          <AlertDialogContent data-testid="regen-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>{t("regen_confirm_title")}</AlertDialogTitle>
              <AlertDialogDescription>{t("regen_confirm_body")}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="regen-cancel">{t("cancel")}</AlertDialogCancel>
              <AlertDialogAction onClick={(e) => { e.preventDefault(); regenerateJourney(); }} disabled={regenBusy} className="bg-[#3F6E85] hover:bg-[#2B4A59] gap-2" data-testid="regen-confirm-yes">
                {regenBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} {t("regen_confirm_yes")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Button data-testid="profile-save" onClick={save} disabled={busy} className="rounded-full bg-[#3F6E85] hover:bg-[#2B4A59] h-11 px-8">
          {busy ? "..." : t("profile_save")}
        </Button>
      </div>
    </div>
  );
}
